import { useEffect, useRef, useState } from "react";

import { DONATION_ARRIVAL_CLEAR_TIME } from "../utils/donationAnimation";

/*
  Paces donation cards into the activity feed.

  Totals, leaderboards and the progress bar are NOT gated by this —
  they update the moment the websocket message lands. This queue only
  controls how fast cards appear in the middle feed, so the celebration
  moment survives without the headline number ever going stale.

  Burst valve: a normal trickle plays at full length; a backlog speeds
  up; a large backlog collapses into a single summary card so the feed
  can never fall permanently behind a busy campaign.
*/

export const DONATION_QUEUE_DISPLAY_TIME = 5000;
const DONATION_QUEUE_FAST_TIME = 1600;

const SPEED_UP_THRESHOLD = 3;
const COLLAPSE_THRESHOLD = 8;

function saleFeedItem(sale, batchId, isNew) {
  return {
    kind: "sale",
    feedId: `donation-feed-sale-${sale.id}`,
    batchId,
    isNew,
    sale,
  };
}

function groupFeedItem(sales, batchId) {
  const total = sales.reduce((sum, sale) => sum + (sale.paymentAmount || 0), 0);

  return {
    kind: "group",
    feedId: `donation-feed-group-${batchId}`,
    batchId,
    isNew: true,
    count: sales.length,
    total,
    sales,
  };
}

const byNewestFirst = (a, b) =>
  a.created > b.created ? -1 : a.created < b.created ? 1 : 0;

export default function useDonationQueue(sales, isLoading) {
  const [feedItems, setFeedItems] = useState([]);
  const [activeBatchId, setActiveBatchId] = useState(null);

  const queueRef = useRef([]);
  const seenRef = useRef(new Set());
  const initializedRef = useRef(false);
  const batchCounterRef = useRef(0);
  const timerRef = useRef(null);
  const drainingRef = useRef(false);
  const arrivalTimersRef = useRef([]);

  useEffect(() => {
    if (isLoading || !Array.isArray(sales)) return;

    const incoming = sales.filter((sale) => sale && !seenRef.current.has(sale.id));

    /*
      Everything present once the initial fetch settles is history, not
      an arrival — render it at once with no animation. This runs even
      when there are no sales yet, so a campaign that starts empty still
      animates its very first live donation.
    */
    if (!initializedRef.current) {
      initializedRef.current = true;

      incoming.forEach((sale) => seenRef.current.add(sale.id));

      setFeedItems(
        [...incoming]
          .sort(byNewestFirst)
          .map((sale) => saleFeedItem(sale, null, false)),
      );
      return;
    }

    if (!incoming.length) return;

    incoming.forEach((sale) => seenRef.current.add(sale.id));
    queueRef.current.push(...[...incoming].sort(byNewestFirst).reverse());
  }, [sales, isLoading]);

  useEffect(() => {
    if (drainingRef.current) return;

    const drain = () => {
      const queue = queueRef.current;

      if (!queue.length) {
        drainingRef.current = false;
        timerRef.current = null;
        return;
      }

      drainingRef.current = true;
      batchCounterRef.current += 1;

      const batchId = `donation-batch-${batchCounterRef.current}`;
      let item;
      let delay;

      if (queue.length > COLLAPSE_THRESHOLD) {
        // Too far behind to play individually — collapse the backlog.
        const remaining = queue.splice(0, queue.length);
        item = groupFeedItem(remaining, batchId);
        delay = DONATION_QUEUE_FAST_TIME;
      } else {
        item = saleFeedItem(queue.shift(), batchId, true);
        delay =
          queue.length > SPEED_UP_THRESHOLD
            ? DONATION_QUEUE_FAST_TIME
            : DONATION_QUEUE_DISPLAY_TIME;
      }

      setFeedItems((previous) => [
        item,
        ...previous.map((existing) =>
          existing.isNew ? { ...existing, isNew: false } : existing,
        ),
      ]);
      setActiveBatchId(batchId);

      // Drop the arrival class once its animation has finished, so a
      // later re-render can't leave a stale animating card behind.
      arrivalTimersRef.current.push(
        window.setTimeout(() => {
          setFeedItems((previous) =>
            previous.map((existing) =>
              existing.batchId === batchId
                ? { ...existing, isNew: false }
                : existing,
            ),
          );
        }, DONATION_ARRIVAL_CLEAR_TIME),
      );

      timerRef.current = window.setTimeout(drain, delay);
    };

    // Poll for work; the sales effect above only fills the queue.
    const poll = window.setInterval(() => {
      if (!drainingRef.current && queueRef.current.length) drain();
    }, 250);

    return () => {
      window.clearInterval(poll);
      if (timerRef.current) window.clearTimeout(timerRef.current);
      arrivalTimersRef.current.forEach((timer) => window.clearTimeout(timer));
      arrivalTimersRef.current = [];
      drainingRef.current = false;
    };
  }, []);

  return { feedItems, activeBatchId };
}
